// src/pages/FundraisingPage.js
import { useState, useEffect } from 'react';

import { useAuth } from '../context/AuthContext';
import { getFundraising, createFundraisingCampaign } from '../api';
import NavBar from '../components/shared/NavBar';

const NAVY  = '#1B3A6B';
const GOLD  = '#C9A84C';
const GREEN = '#1a6b3a';
const RED   = '#c0392b';

export default function FundraisingPage() {
  const { role }       = useAuth();
  const isExecutive    = role === 'executive';

  const [campaigns, setCampaigns] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [showForm,  setShowForm]  = useState(false);
  const [posting,   setPosting]   = useState(false);

  const [form, setForm] = useState({
    campaignName: '',
    goal:         '',
    currency:     'GHS',
    startDate:    new Date().toISOString().split('T')[0],
    endDate:      '',
    description:  ''
  });

  const CURRENCIES = ['GHS', 'USD', 'GBP', 'EUR', 'CAD', 'AUD'];

  useEffect(() => {
    loadCampaigns();
  }, []);

  async function loadCampaigns() {
    setLoading(true);
    try {
      const result = await getFundraising();
      setCampaigns(result.campaigns || []);
    } catch (err) {
      setError('Could not load campaigns: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  const handlePost = async () => {
    setError('');
    if (!form.campaignName.trim()) {
      setError('Campaign name is required.');
      return;
    }
    if (!form.goal || isNaN(Number(form.goal)) ||
        Number(form.goal) <= 0) {
      setError('Please enter a valid goal amount.');
      return;
    }

    setPosting(true);
    try {
      await createFundraisingCampaign({
        ...form,
        goal: Number(form.goal)
      });
      setSuccess('Campaign created successfully!');
      setShowForm(false);
      setForm({
        campaignName: '',
        goal:         '',
        currency:     'GHS',
        startDate:    new Date().toISOString().split('T')[0],
        endDate:      '',
        description:  ''
      });
      await loadCampaigns();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError('Failed to create campaign: ' + err.message);
    } finally {
      setPosting(false);
    }
  };


  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getDaysLeft = (endDate) => {
    if (!endDate) return null;
    try {
      const end  = new Date(endDate);
      const now  = new Date();
      const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return diff;
    } catch {
      return null;
    }
  };

  const activeCampaigns = campaigns.filter(
    c => c.status === 'active'
  );
  const closedCampaigns = campaigns.filter(
    c => c.status !== 'active'
  );

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav */}
      <NavBar />

      <div style={{
        maxWidth: '800px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '20px', color: '#fff',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: '12px'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
              🎯 Fundraising
            </h2>
            <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
              {loading ? 'Loading...'
                : `${activeCampaigns.length} active campaign${activeCampaigns.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {isExecutive && (
            <button
              onClick={() => {
                setShowForm(!showForm);
                setError('');
              }}
              style={{
                background: GOLD, color: NAVY,
                border: 'none', borderRadius: '8px',
                padding: '10px 20px', cursor: 'pointer',
                fontWeight: 'bold', fontSize: '14px'
              }}
            >
              {showForm ? '✕ Cancel' : '+ New Campaign'}
            </button>
          )}
        </div>

        {/* Success */}
        {success && (
          <div style={{
            background: '#d4edda', border: '1px solid #c3e6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: GREEN, fontSize: '14px',
            fontWeight: '600'
          }}>
            ✓ {success}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: RED, fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* New Campaign Form */}
        {showForm && isExecutive && (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '24px', marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: `2px solid ${GOLD}`
          }}>
            <h3 style={{
              color: NAVY, margin: '0 0 20px', fontSize: '16px'
            }}>
              New Fundraising Campaign
            </h3>

            <div style={{
              display: 'flex', flexDirection: 'column', gap: '16px'
            }}>

              {/* Campaign Name */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555',
                  marginBottom: '6px'
                }}>
                  Campaign Name *
                </label>
                <input
                  type="text"
                  value={form.campaignName}
                  onChange={e => setForm(p => ({
                    ...p, campaignName: e.target.value
                  }))}
                  placeholder="e.g. Scholarship Fund 2024"
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px', fontSize: '14px',
                    outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Goal + Currency */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block', fontSize: '13px',
                    fontWeight: '600', color: '#555',
                    marginBottom: '6px'
                  }}>
                    Goal Amount *
                  </label>
                  <input
                    type="number"
                    value={form.goal}
                    onChange={e => setForm(p => ({
                      ...p, goal: e.target.value
                    }))}
                    placeholder="0"
                    min="0"
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px', fontSize: '14px',
                      outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ width: '120px' }}>
                  <label style={{
                    display: 'block', fontSize: '13px',
                    fontWeight: '600', color: '#555',
                    marginBottom: '6px'
                  }}>
                    Currency
                  </label>
                  <select
                    value={form.currency}
                    onChange={e => setForm(p => ({
                      ...p, currency: e.target.value
                    }))}
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px', fontSize: '14px',
                      outline: 'none', background: '#fff',
                      boxSizing: 'border-box'
                    }}
                  >
                    {CURRENCIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Dates */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block', fontSize: '13px',
                    fontWeight: '600', color: '#555',
                    marginBottom: '6px'
                  }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={e => setForm(p => ({
                      ...p, startDate: e.target.value
                    }))}
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px', fontSize: '14px',
                      outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block', fontSize: '13px',
                    fontWeight: '600', color: '#555',
                    marginBottom: '6px'
                  }}>
                    End Date (optional)
                  </label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={e => setForm(p => ({
                      ...p, endDate: e.target.value
                    }))}
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px', fontSize: '14px',
                      outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555',
                  marginBottom: '6px'
                }}>
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(p => ({
                    ...p, description: e.target.value
                  }))}
                  placeholder="Describe the purpose of this campaign..."
                  rows={4}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px', fontSize: '14px',
                    outline: 'none', boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'Arial, sans-serif'
                  }}
                />
              </div>

              {/* Submit */}
              <button
                onClick={handlePost}
                disabled={posting}
                style={{
                  padding: '12px 24px',
                  background: posting ? '#ccc' : NAVY,
                  color: '#fff', border: 'none',
                  borderRadius: '8px',
                  cursor: posting ? 'not-allowed' : 'pointer',
                  fontSize: '15px', fontWeight: 'bold',
                  alignSelf: 'flex-end'
                }}
              >
                {posting ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div style={{
            textAlign: 'center', padding: '60px',
            color: '#666', fontSize: '14px'
          }}>
            Loading campaigns...
          </div>
        ) : campaigns.length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '60px', textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              🎯
            </div>
            <h3 style={{ color: NAVY, margin: '0 0 8px' }}>
              No campaigns yet
            </h3>
            <p style={{ color: '#666', margin: 0 }}>
              {isExecutive
                ? 'Create your first fundraising campaign above.'
                : 'Check back later for fundraising campaigns.'}
            </p>
          </div>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: '20px'
          }}>

            {/* Active Campaigns */}
            {activeCampaigns.length > 0 && (
              <>
                <h3 style={{
                  color: NAVY, margin: '0',
                  fontSize: '16px'
                }}>
                  Active Campaigns
                </h3>
                {activeCampaigns.map(campaign => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    formatDate={formatDate}
                    getDaysLeft={getDaysLeft}
                  />
                ))}
              </>
            )}

            {/* Closed Campaigns */}
            {closedCampaigns.length > 0 && (
              <>
                <h3 style={{
                  color: '#888', margin: '8px 0 0',
                  fontSize: '16px'
                }}>
                  Past Campaigns
                </h3>
                {closedCampaigns.map(campaign => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    formatDate={formatDate}
                    getDaysLeft={getDaysLeft}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Campaign Card Component ───────────────────────────────────
function CampaignCard({ campaign, formatDate, getDaysLeft }) {
  const [expanded, setExpanded] = useState(false);

  const goal    = Number(campaign.goal)   || 0;
  const raised  = Number(campaign.raised) || 0;
  const pct     = goal > 0
    ? Math.min(Math.round((raised / goal) * 100), 100) : 0;
  const daysLeft = getDaysLeft(campaign.endDate);
  const isActive = campaign.status === 'active';

  const progressColor = pct >= 100 ? '#1a6b3a'
    : pct >= 75 ? '#2ecc71'
    : pct >= 50 ? '#C9A84C'
    : '#e74c3c';

  return (
    <div style={{
      background: '#fff', borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      overflow: 'hidden',
      border: isActive
        ? '2px solid transparent'
        : '2px solid #e0e0e0',
      opacity: isActive ? 1 : 0.8
    }}>

      {/* Card Header */}
      <div style={{
        background: isActive
          ? `linear-gradient(135deg, #1B3A6B 0%, #2d5a9e 100%)`
          : '#f8f9fa',
        padding: '20px 24px'
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div>
            <h3 style={{
              margin: '0 0 6px',
              color: isActive ? '#fff' : '#333',
              fontSize: '18px', fontWeight: 'bold'
            }}>
              {campaign.campaignName}
            </h3>
            <div style={{
              display: 'flex', gap: '12px',
              flexWrap: 'wrap'
            }}>
              <span style={{
                fontSize: '13px',
                color: isActive
                  ? 'rgba(255,255,255,0.8)' : '#888'
              }}>
                📅 Started {formatDate(campaign.startDate)}
              </span>
              {campaign.endDate && (
                <span style={{
                  fontSize: '13px',
                  color: isActive
                    ? 'rgba(255,255,255,0.8)' : '#888'
                }}>
                  🏁 Ends {formatDate(campaign.endDate)}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isActive && daysLeft !== null && (
              <span style={{
                background: daysLeft <= 7
                  ? '#fdecea' : '#fff3cd',
                color: daysLeft <= 7 ? '#c0392b' : '#856404',
                padding: '4px 12px', borderRadius: '12px',
                fontSize: '12px', fontWeight: 'bold'
              }}>
                {daysLeft <= 0
                  ? 'Ended'
                  : daysLeft === 1
                  ? '1 day left'
                  : `${daysLeft} days left`}
              </span>
            )}
            {!isActive && (
              <span style={{
                background: '#e0e0e0', color: '#888',
                padding: '4px 12px', borderRadius: '12px',
                fontSize: '12px', fontWeight: 'bold'
              }}>
                Closed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div style={{ padding: '20px 24px' }}>

        {/* Amount display */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline', marginBottom: '12px',
          flexWrap: 'wrap', gap: '8px'
        }}>
          <div>
            <span style={{
              fontSize: '28px', fontWeight: 'bold',
              color: progressColor
            }}>
              {campaign.currency} {raised.toLocaleString()}
            </span>
            <span style={{
              fontSize: '15px', color: '#888', marginLeft: '8px'
            }}>
              raised
            </span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: '15px', color: '#555', fontWeight: '600'
            }}>
              Goal: {campaign.currency} {goal.toLocaleString()}
            </div>
            <div style={{
              fontSize: '13px', color: '#888'
            }}>
              {goal - raised > 0
                ? `${campaign.currency} ${(goal - raised).toLocaleString()} remaining`
                : '🎉 Goal reached!'}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{
          height: '16px', background: '#f0f0f0',
          borderRadius: '8px', overflow: 'hidden',
          marginBottom: '8px'
        }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: pct >= 100
              ? `linear-gradient(90deg, #1a6b3a, #2ecc71)`
              : `linear-gradient(90deg, #1B3A6B, #C9A84C)`,
            borderRadius: '8px',
            transition: 'width 0.5s ease'
          }} />
        </div>

        {/* Percentage */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: '14px', fontWeight: 'bold',
            color: progressColor
          }}>
            {pct}% {pct >= 100 ? '🎉' : ''}
          </span>
          {campaign.description && (
            <button
              onClick={() => setExpanded(!expanded)}
              style={{
                background: 'none', border: 'none',
                color: '#1B3A6B', cursor: 'pointer',
                fontSize: '13px', fontWeight: '600'
              }}
            >
              {expanded ? '▲ Less' : '▼ Details'}
            </button>
          )}
        </div>

        {/* Expanded description */}
        {expanded && campaign.description && (
          <div style={{
            marginTop: '16px', padding: '16px',
            background: '#f8f9fa', borderRadius: '8px',
            fontSize: '14px', color: '#555',
            lineHeight: '1.6'
          }}>
            {campaign.description}
          </div>
        )}

        {/* Milestone badges */}
        <div style={{
          display: 'flex', gap: '8px', marginTop: '12px',
          flexWrap: 'wrap'
        }}>
          {[25, 50, 75, 100].map(milestone => (
            <span
              key={milestone}
              style={{
                padding: '3px 10px', borderRadius: '10px',
                fontSize: '11px', fontWeight: 'bold',
                background: pct >= milestone
                  ? '#d4edda' : '#f0f0f0',
                color: pct >= milestone ? '#1a6b3a' : '#aaa'
              }}
            >
              {pct >= milestone ? '✓' : '○'} {milestone}%
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}